import { lazy, Suspense } from 'react'
import { useTour } from '~/lib/tour/state.tsx'

/**
 * Where the walkthrough is actually drawn — once, above the whole shell, because a
 * tour that lived inside a screen would end the moment it walked to the next one.
 */
const Welcome = lazy(() => import('./welcome.tsx').then((m) => ({ default: m.TourWelcome })))
const Overlay = lazy(() => import('./overlay.tsx').then((m) => ({ default: m.TourOverlay })))

/**
 * The welcome's chunk is one round trip behind the session that decides to show it.
 *
 * Without something standing in for it, a new account gets a working app for a beat
 * — long enough on a 4G connection to read a tile and reach for it — and then a
 * full-screen panel arrives on top of the tap. So the panel it is about to become
 * goes up immediately: same background, no content, nothing behind it clickable.
 * The welcome then fades into a screen that was already its own colour rather than
 * interrupting one that was not.
 *
 * The running tour needs no such thing. Nobody is waiting for a step they did not
 * ask for, and the app underneath stays usable until the card arrives.
 */
function Holding() {
  return <div data-theme="dark" className="fixed inset-0 z-70 bg-canvas" aria-hidden />
}

export function TourLayer() {
  const { run, welcomeOpen } = useTour()
  return (
    <>
      {welcomeOpen ? (
        <Suspense fallback={<Holding />}>
          <Welcome />
        </Suspense>
      ) : null}
      {run ? (
        <Suspense fallback={null}>
          <Overlay />
        </Suspense>
      ) : null}
    </>
  )
}
